import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { ListChecks, Search, Eye, CalendarDays, Filter, Loader2, Flag, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { EmptyState } from "@/modules/collector_v2/components/common/EmptyState";
import {
  formatEpochDate,
  gameModeLabel,
  boardToString,
} from "@/modules/collector_v2/lib/replayFormatters";
import { HandSummaryDrawer } from "@/modules/collector_v2/components/profile/HandSummaryDrawer";
import { useFlaggedUids, rowTintForFlag, SEVERITY_LABELS } from "@/modules/collector_v2/hooks/useFlaggedUids";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 100;

const GAME_MODES = [
  { value: "", label: "Todas" },
  { value: "NLH", label: "NLH" },
  { value: "PLO5", label: "PLO5" },
  { value: "PLO6", label: "PLO6" },
  { value: "PLO4", label: "PLO4" },
  { value: "MODE_211", label: "MODE_211" },
  { value: "OFC", label: "OFC" },
];

const GAME_MODE_COLORS = {
  NLH:      { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" },
  PLO4:     { bg: "#faf5ff", text: "#7c3aed", border: "#ddd6fe" },
  PLO5:     { bg: "#fdf2f8", text: "#db2777", border: "#fbcfe8" },
  PLO6:     { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" },
  OFC:      { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
  MODE_211: { bg: "#ecfeff", text: "#0891b2", border: "#a5f3fc" },
};

function GameModeBadge({ mode }) {
  const label = gameModeLabel(mode);
  const colors = GAME_MODE_COLORS[mode] || { bg: "#f3f4f6", text: "#6b7280", border: "#e5e7eb" };
  return (
    <span
      data-testid={`game-mode-badge-${mode}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
        background: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
      }}
    >
      {label}
    </span>
  );
}

export default function HandsPage() {
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(params.get("q") || "");
  const [tableId, setTableId] = useState(params.get("table_id") || "");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [gameMode, setGameMode] = useState(params.get("game_mode") || "");
  const [bb, setBb] = useState(params.get("bb") || "");
  const [stakeOptions, setStakeOptions] = useState([]);
  const [drawerHandId, setDrawerHandId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 🚩 Filtro flagged: "" | "true" | "match"
  const [flagged, setFlagged] = useState(params.get("flagged") || "");
  const [stats, setStats] = useState(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);
  const filterKey = useMemo(() => JSON.stringify({ q, tableId, dateFrom, dateTo, gameMode, bb, flagged }), [q, tableId, dateFrom, dateTo, gameMode, bb, flagged]);

  // 🚩 Watchlist de UIDs flagged (auto-cacheada 5min)
  const { computeRowFlag, flaggedMap } = useFlaggedUids();

  // Stats de flagged/match (ventana 24h por default)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/v2/hands/flagged-stats?window_hours=24`)
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch(() => {});
    const interval = setInterval(() => {
      axios.get(`${API}/api/v2/hands/flagged-stats?window_hours=24`)
        .then((res) => { if (!cancelled) setStats(res.data); })
        .catch(() => {});
    }, 60000); // refresh cada 60s
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Carga la lista de stakes únicos (SB/BB) una sola vez para poblar el dropdown.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/v2/hands/stakes`)
      .then((res) => {
        if (cancelled) return;
        setStakeOptions(res.data?.items || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buildUrl = useCallback((skip) => {
    const search = new URLSearchParams();
    search.set("limit", String(PAGE_SIZE));
    search.set("skip", String(skip));
    if (q.trim()) search.set("q", q.trim());
    if (tableId.trim() && Number(tableId) > 0) search.set("table_id", tableId.trim());
    if (dateFrom) search.set("date_from", dateFrom);
    if (dateTo) search.set("date_to", dateTo);
    if (gameMode) search.set("game_mode", gameMode);
    if (bb && Number(bb) > 0) search.set("bb", bb);
    if (flagged) search.set("flagged", flagged);
    return `${API}/api/v2/hands?${search.toString()}`;
  }, [q, tableId, dateFrom, dateTo, gameMode, bb, flagged]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    setHasMore(true);
    axios.get(buildUrl(0))
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setHasMore((data.items || []).length >= PAGE_SIZE);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Error cargando manos");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filterKey, buildUrl]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    axios.get(buildUrl(items.length))
      .then((res) => {
        const data = res.data;
        const newItems = data.items || [];
        setItems((prev) => [...prev, ...newItems]);
        setTotal(data.total || 0);
        setHasMore(newItems.length >= PAGE_SIZE);
        setLoadingMore(false);
      })
      .catch(() => { setLoadingMore(false); });
  }, [loadingMore, hasMore, buildUrl, items.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const openHandDrawer = (handId) => {
    setDrawerHandId(handId);
    setDrawerOpen(true);
  };

  const clearFilters = () => {
    setQ(""); setTableId(""); setDateFrom(""); setDateTo(""); setGameMode(""); setBb(""); setFlagged("");
  };

  const hasFilters = q || tableId || dateFrom || dateTo || gameMode || bb || flagged;

  return (
    <>
      <PageHeader
        eyebrow="Manos capturadas"
        title="Manos V2"
        description="Listado de manos procesadas por Collector V2. Click Ver abre el detalle en modal."
      />

      {/* Stats card 🚩 — últimas 24h */}
      {stats && (
        <div
          data-testid="hands-flagged-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <div style={statBox()}>
            <span style={statLabel}>Manos · últimas 24h</span>
            <span style={statValue}>{stats.total?.toLocaleString("es-AR") || 0}</span>
          </div>
          <div
            style={statBox("#fef3c7")}
            data-testid="hands-stats-flagged"
            onClick={() => setFlagged(flagged === "true" ? "" : "true")}
            title="Filtrar solo manos con UIDs en watchlist"
          >
            <span style={{ ...statLabel, color: "#a16207" }}>🚩 Manos flagged (24h)</span>
            <span style={{ ...statValue, color: "#b45309" }}>{stats.flagged?.toLocaleString("es-AR") || 0}</span>
            <span style={{ fontSize: 10, color: "#a16207" }}>
              {stats.total > 0 ? `${((stats.flagged / stats.total) * 100).toFixed(1)}%` : "—"} · click para filtrar
            </span>
          </div>
          <div
            style={statBox("#fee2e2")}
            data-testid="hands-stats-match"
            onClick={() => setFlagged(flagged === "match" ? "" : "match")}
            title="Filtrar solo manos con par MATCH (los 2 UIDs vinculados juntos)"
          >
            <span style={{ ...statLabel, color: "#991b1b" }}>🔴 MATCH (24h)</span>
            <span style={{ ...statValue, color: "#b91c1c" }}>{stats.match?.toLocaleString("es-AR") || 0}</span>
            <span style={{ fontSize: 10, color: "#991b1b" }}>evidencia hard · click para filtrar</span>
          </div>
        </div>
      )}

      {/* Barra de filtros sticky */}
      <div
        data-testid="hands-sticky-bar"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "#f3f4f6",
          borderBottom: "1px solid #e5e7eb",
          padding: "10px 0",
          marginBottom: 12,
        }}
      >
        <Card data-testid="hands-filter-card" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 flex-1 min-w-[160px] max-w-[280px] border rounded-md px-2.5 py-1.5 bg-gray-50">
                <Search className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <input
                  data-testid="hands-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="hand_id..."
                  className="bg-transparent border-0 outline-none text-sm w-full placeholder:text-gray-400"
                />
              </div>

              <div className="flex items-center gap-1.5 w-[110px] border rounded-md px-2.5 py-1.5 bg-gray-50">
                <input
                  data-testid="hands-table-input"
                  type="number"
                  value={tableId}
                  onChange={(e) => setTableId(e.target.value)}
                  placeholder="Table ID"
                  className="bg-transparent border-0 outline-none text-sm w-full placeholder:text-gray-400"
                />
              </div>

              <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-gray-50">
                <Filter className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <select
                  data-testid="hands-game-mode-filter"
                  value={gameMode}
                  onChange={(e) => setGameMode(e.target.value)}
                  className="bg-transparent border-0 outline-none text-sm cursor-pointer"
                  style={{ minWidth: 80 }}
                >
                  {GAME_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de stakes (SB/BB) — populado desde /api/v2/hands/stakes */}
              <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-gray-50">
                <span className="text-[11px] text-gray-400 font-medium tracking-wide select-none">SB/BB</span>
                <select
                  data-testid="hands-stake-filter"
                  value={bb}
                  onChange={(e) => setBb(e.target.value)}
                  className="bg-transparent border-0 outline-none text-sm cursor-pointer tabular-nums"
                  style={{ minWidth: 90 }}
                >
                  <option value="">Todos</option>
                  {stakeOptions.map((s) => (
                    <option key={`${s.sb}-${s.bb}`} value={String(s.bb)}>
                      {s.sb}/{s.bb}
                    </option>
                  ))}
                </select>
              </div>

              <div className="h-6 w-px bg-gray-200 mx-0.5 hidden sm:block" />

              <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-gray-50">
                <CalendarDays className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <input
                  data-testid="hands-date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-transparent border-0 outline-none text-sm"
                />
              </div>

              <span className="text-xs text-gray-400">a</span>

              <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 bg-gray-50">
                <CalendarDays className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <input
                  data-testid="hands-date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-transparent border-0 outline-none text-sm"
                />
              </div>

              {hasFilters && (
                <Button data-testid="hands-clear-filters" variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-gray-500">
                  Limpiar
                </Button>
              )}

              {/* Toggle chips para flagged/match */}
              <div className="flex items-center gap-1 ml-1">
                <Button
                  data-testid="hands-toggle-flagged"
                  size="sm"
                  variant={flagged === "true" ? "default" : "outline"}
                  onClick={() => setFlagged(flagged === "true" ? "" : "true")}
                  className="h-7 text-xs gap-1"
                  style={flagged === "true" ? { background: "#f59e0b", color: "#fff" } : { color: "#a16207", borderColor: "#fbbf24" }}
                >
                  <Flag className="h-3 w-3" /> Flagged
                </Button>
                <Button
                  data-testid="hands-toggle-match"
                  size="sm"
                  variant={flagged === "match" ? "default" : "outline"}
                  onClick={() => setFlagged(flagged === "match" ? "" : "match")}
                  className="h-7 text-xs gap-1"
                  style={flagged === "match" ? { background: "#dc2626", color: "#fff" } : { color: "#b91c1c", borderColor: "#fca5a5" }}
                >
                  🚩 MATCH
                </Button>
              </div>

              {/* Contador de resultados inline */}
              <div className="ml-auto text-xs text-gray-500 tabular-nums whitespace-nowrap" data-testid="hands-result-count">
                {loading ? "Cargando..." : `${items.length} / ${total.toLocaleString()} manos`}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading && <LoadingState />}
      {error && <ErrorState message={error} onRetry={() => window.location.reload()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          title="Sin manos para mostrar"
          description="Ajusta los filtros o espera que el Collector V2 capture nuevas manos."
          icon={ListChecks}
          testid="hands-empty-state"
        />
      )}
      {!loading && !error && items.length > 0 && (
        <Card data-testid="hands-table-card">
          <CardContent className="pt-0 px-0">
            <Table data-testid="hands-table" aria-label="Tabla de manos V2">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky top-0 bg-white z-10" style={{width:"28%"}}>Hand ID</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10" style={{width:"22%"}}>Mesa</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10" style={{width:"10%"}}>Juego</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10 text-center" style={{width:"8%"}}>Jug.</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10" style={{width:"10%"}}>Flags</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10" style={{width:"14%"}}>Fecha</TableHead>
                  <TableHead className="sticky top-0 bg-white z-10 text-right" style={{width:"8%"}}>Accion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((h) => {
                  const flag = computeRowFlag(h.player_uids || []);
                  const tint = rowTintForFlag(flag);
                  const flaggedTooltip = flag.hasFlag
                    ? flag.isMatch
                      ? `🚩 MATCH — ${flag.matchPairs.map(([a, b]) => `${a}⟷${b}`).join(", ")} (jugadores vinculados juntos en esta mano)`
                      : `🚩 ${flag.flaggedPlayers.length} jugador(es) en watchlist · severidad max: ${SEVERITY_LABELS[flag.maxSeverity] || "—"} · UIDs: ${flag.flaggedPlayers.map((p) => p.uid).join(", ")}`
                    : undefined;
                  return (
                  <TableRow
                    key={h.hand_id}
                    data-testid="hands-table-row"
                    data-flagged={flag.hasFlag ? "1" : "0"}
                    data-match={flag.isMatch ? "1" : "0"}
                    data-severity={flag.maxSeverity || ""}
                    style={tint ? { background: tint } : undefined}
                    title={flaggedTooltip}
                  >
                    <TableCell className="font-mono text-[11px] break-all max-w-[260px]" title={h.hand_id}>
                      {h.hand_id}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium">
                            {h.table_name || `Mesa #${h.table_id}`}
                          </span>
                          {(h.small_blind_visual != null && h.big_blind_visual != null) && (
                            <button
                              type="button"
                              onClick={() => setBb(String(h.big_blind_visual))}
                              data-testid="hand-row-blinds-badge"
                              title={`Filtrar por SB/BB: ${h.small_blind_visual}/${h.big_blind_visual}`}
                              className="text-[10px] tabular-nums px-1.5 py-0 h-4 font-medium border border-slate-300 text-slate-600 rounded bg-white hover:bg-slate-100 hover:border-slate-400 transition-colors cursor-pointer"
                            >
                              {h.small_blind_visual}/{h.big_blind_visual}
                            </button>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400">
                          #{h.table_id} · Club {h.club_id ?? "\u2014"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <GameModeBadge mode={h.game_mode} />
                    </TableCell>
                    <TableCell className="tabular-nums text-center">
                      {h.num_players ?? "\u2014"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {flag.isMatch && (
                          <Badge variant="destructive" className="text-[10px] gap-1" data-testid={`hand-row-match-${h.hand_id}`}>
                            <Flag className="h-3 w-3" /> MATCH
                          </Badge>
                        )}
                        {flag.hasFlag && !flag.isMatch && (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1"
                            data-testid={`hand-row-flagged-${h.hand_id}`}
                            style={{
                              borderColor: flag.maxSeverity === "high" ? "#dc2626" : flag.maxSeverity === "medium" ? "#f97316" : "#eab308",
                              color: flag.maxSeverity === "high" ? "#dc2626" : flag.maxSeverity === "medium" ? "#f97316" : "#a16207",
                            }}
                          >
                            <Star className="h-3 w-3" /> {flag.flaggedPlayers.length}
                          </Badge>
                        )}
                        {h.is_bomb_pot && <Badge variant="secondary" className="text-[10px]">Bomb</Badge>}
                        {h.is_double_board && <Badge variant="secondary" className="text-[10px]">2-Board</Badge>}
                        {h.is_jackpot_hand && <Badge variant="secondary" className="text-[10px]">Jackpot</Badge>}
                        {h.is_evchop && <Badge variant="secondary" className="text-[10px]">EV-Chop</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                      {formatEpochDate(h.started_at_raw)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`hand-row-open-${h.hand_id}`}
                        onClick={() => openHandDrawer(h.hand_id)}
                        className="h-7 text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div ref={sentinelRef} className="py-4 flex justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando mas...
                </div>
              )}
              {!loadingMore && hasMore && (
                <Button variant="outline" size="sm" onClick={loadMore} data-testid="hands-load-more">
                  Cargar mas ({items.length} / {total.toLocaleString()})
                </Button>
              )}
              {!hasMore && items.length > 0 && (
                <p className="text-xs text-gray-400">Todas las manos cargadas ({items.length.toLocaleString()})</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      <HandSummaryDrawer
        handId={drawerHandId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </>
  );
}

// Helpers de estilo para la stats card
const statBox = (bg) => ({
  background: bg || "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  cursor: bg ? "pointer" : "default",
  transition: "transform 0.1s",
});
const statLabel = {
  fontSize: 10,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  fontWeight: 600,
};
const statValue = {
  fontSize: 22,
  fontWeight: 700,
  fontFamily: "monospace",
  color: "#0f172a",
};
